import React, { useEffect } from 'react';
import { Checkbox, Grid2 as Grid, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import { Controller, useController, useWatch } from 'react-hook-form';
import { get, has, uniq } from 'lodash';

import { FormQuestionOption } from '../../../../../../../Utils/Types/FormBuilder';
import SelectAutocomplete from '../../../../../../DesignSystem/SelectAutocomplete';
import { QuestionTypeShowProps } from './QuestionShow';
import TextShow from './TextShow';
import { checkOtherCheckbox, remapCheckBoxesValues } from './utils';

export default function CheckboxShow({
  question,
  errors,
  index,
  lang,
  disabled,
  form,
  scope,
}: QuestionTypeShowProps) {
  const { control } = form;
  const intl = useIntl();
  const inputNamePrefix = `${scope}answers_attributes.${index}`;
  const answerAttributes = useWatch({ control, name: inputNamePrefix });
  const name = `${inputNamePrefix}.question_option_ids`;
  const questionOptionsIdsController = useController({
    control,
    name,
  });
  const { field: contentField } = useController({
    control,
    name: `${inputNamePrefix}.content_i18n_${lang}`,
  });

  useEffect(() => {
    if (answerAttributes?.content?.length > 0) {
      // get the question options id from question_options with other = true and inject to answers_attributes.question_option_ids
      checkOtherCheckbox(question, questionOptionsIdsController, answerAttributes);
    }
  }, [answerAttributes?.content?.length]);

  useEffect(() => {
    // Clear question_option_ids if content is filled
    if (contentField?.value?.length > 0) {
      const otherQuestionOption = question?.question_options?.find(
        (questionOption: FormQuestionOption) => questionOption.other,
      );

      const selectedOptionIds = answerAttributes.question_option_ids || [];
      const updatedOptionIds = uniq([...selectedOptionIds, otherQuestionOption?.id]);

      questionOptionsIdsController.field.onChange(updatedOptionIds);
    }
  }, [contentField?.value]);

  useEffect(() => {
    if (answerAttributes?.question_option_ids?.length > 0) {
      questionOptionsIdsController.field.onChange(answerAttributes.question_option_ids);
    }
  }, [answerAttributes?.question_option_ids?.length]);

  const controlledCheckbox = () => (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <FormGroup aria-labelledby={`checkbox-group-question-${question.id}`}>
          {question.question_options?.map((questionOption: FormQuestionOption) => {
            const isChecked = (value || []).includes(questionOption.id);

            if (questionOption.other) {
              return (
                <Grid container spacing={1} key={`${questionOption.id}-${question.id}`}>
                  <Grid>
                    <FormControlLabel
                      value={questionOption.id}
                      control={
                        <Checkbox
                          onChange={() => {
                            remapCheckBoxesValues(value, isChecked, questionOption, onChange);
                          }}
                          aria-checked={isChecked}
                        />
                      }
                      label={intl.formatMessage({ id: 'POST_FORM.FORM_BUILDER.OPTIONS.OTHER' })}
                      aria-label={intl.formatMessage({
                        id: 'POST_FORM.FORM_BUILDER.OPTIONS.OTHER',
                      })}
                      checked={isChecked}
                      disabled={disabled}
                      sx={{ mr: 0 }}
                    />
                  </Grid>
                  <Grid>
                    <TextShow
                      lang={lang}
                      scope={scope}
                      size="small"
                      question={question}
                      form={form}
                      errors={errors}
                      index={index}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              );
            }

            return (
              <FormControlLabel
                key={`${questionOption.id}-${question.id}`}
                checked={isChecked}
                disabled={disabled}
                control={
                  <Checkbox
                    onChange={() => {
                      remapCheckBoxesValues(value, isChecked, questionOption, onChange);
                    }}
                    aria-checked={isChecked}
                  />
                }
                label={questionOption.label || ''}
                aria-label={questionOption.label || 'Option'}
              />
            );
          })}
        </FormGroup>
      )}
    />
  );

  const controlledDropdown = () => {
    const getOptionLabel = (option: any) =>
      option?.label || question.question_options.find((o: any) => o.id === option)?.label || '';

    return (
      <SelectAutocomplete
        errors={errors}
        getOptionLabel={getOptionLabel}
        form={form}
        name={name}
        options={question.question_options.map((option) => option.id)}
        placeholder={intl.formatMessage({ id: 'POST_FORM.FORM_BUILDER.PLACEHOLDER' })}
        renderOption={(props: any, option: any, { selected }: any) => (
          <li {...props}>
            <Checkbox
              icon={<CheckBoxOutlineBlank fontSize="small" />}
              checkedIcon={<CheckBox fontSize="small" />}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {getOptionLabel(option)}
          </li>
        )}
        disabled={disabled}
        multiple
        disableCloseOnSelect
        inputProps={{
          'aria-label': question?.title,
        }}
      />
    );
  };

  return (
    <>
      {has(errors, `${inputNamePrefix}.question_options`) && (
        <Typography color="error" variant="body2" role="alert">
          {get(errors, `${inputNamePrefix}.question_options`)?.message}
        </Typography>
      )}

      {question.dropdown ? controlledDropdown() : controlledCheckbox()}
    </>
  );
}
